import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import ACL_RELATIONSHIPS from "acl-relationships";
import { Button } from "@/components/ui/button";
import Authorized from "components/Authorized";
import { getNextQueryParams } from "components/helpers";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import RoleService from "services/Role";
import {
  invalidateRolesQuery,
  useRoleMutations,
  useRolesTable,
} from "hooks/useRolesTable";
import { ROLES_PATH, ROLES_TABLE_CONSTANTS } from "./RolesTableConstants";

const CREATE_ACLS = [ACL_RELATIONSHIPS.adminRole.create];

const RolesTableV3 = () => {
  const navigate = useNavigate();
  const [roleToDelete, setRoleToDelete] = useState(null);
  const { remove } = useRoleMutations();

  const handleEdit = useCallback(
    role => navigate(`${ROLES_PATH}/${role.id}/edit`),
    [navigate]
  );
  const handleClone = useCallback(
    role => navigate(`${ROLES_PATH}/new?from=${role.id}`),
    [navigate]
  );

  const { columns } = useRolesTable({
    onEdit: handleEdit,
    onClone: handleClone,
    onDelete: setRoleToDelete,
  });

  const fetchRoles = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);
        return await RoleService.getRolesTableAsAdmin(nextQueryParams);
      } catch (error) {
        console.error("Error fetching roles:", error);
        throw error;
      }
    },
    [columns]
  );

  return (
    <>
      <ConfirmDeleteDialog
        open={!!roleToDelete}
        onOpenChange={open => !open && setRoleToDelete(null)}
        title="Delete Role"
        description={
          <>
            Are you sure you want to delete the role{" "}
            <strong>{roleToDelete?.name}</strong>?
          </>
        }
        onConfirm={() =>
          !remove.isPending &&
          remove.mutate(roleToDelete.id, {
            onSuccess: () => setRoleToDelete(null),
          })
        }
      />
      <BaseTableV3
        columns={columns}
        queryKey={ROLES_TABLE_CONSTANTS.queryKey}
        className={ROLES_TABLE_CONSTANTS.className}
        tableName={ROLES_TABLE_CONSTANTS.tableName}
        header={ROLES_TABLE_CONSTANTS.header}
        headerIcon={ROLES_TABLE_CONSTANTS.headerIcon}
        createButton={
          <Authorized permissions={CREATE_ACLS}>
            <Button size="sm" onClick={() => navigate(`${ROLES_PATH}/new`)}>
              New Role
            </Button>
          </Authorized>
        }
        fetchData={fetchRoles}
        invalidateData={invalidateRolesQuery}
      />
    </>
  );
};

export default RolesTableV3;
